import { useQuery } from "@tanstack/react-query";
import { Download, ChevronLeft, ChevronRight, Plus, SlidersHorizontal } from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { useState } from "react";
import { get, API, toQS, withToken } from "@/lib/api";
import { useUrlFilters } from "@/lib/useUrlFilters";
import { Filters, filtersToParams } from "@/components/Filters";
import { EventTable } from "@/components/EventTable";
import { Button } from "@/components/ui/button";
import { ManualEventDialog } from "@/pages/ManualEvent";

export default function Events() {
  const { f, set, toggleList, reset } = useUrlFilters();
  const [addOpen, setAddOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const sort = f.sort || "date";
  const order = Number(f.order || 1);
  const page = Number(f.page || 1);
  const params = filtersToParams(f);
  const { data, isFetching } = useQuery({
    queryKey: ["events", params, sort, order, page],
    queryFn: () => get("/events", { ...params, sort, order, page, page_size: 100 }),
    placeholderData: (prev) => prev,
  });
  const onSort = (k) => set({ sort: k, order: sort === k ? -order : 1 });
  const exportUrl = (fmt) => withToken(`${API}/events/export?${toQS({ ...params, sort, order, format: fmt })}`);
  const pages = data ? Math.max(1, Math.ceil(data.total / 100)) : 1;

  return (
    <div className="flex h-[calc(100dvh-var(--chrome-h))]">
      <aside className="hidden lg:block w-64 shrink-0 border-r border-slate-200 dark:border-slate-800 overflow-y-auto p-4 bg-white dark:bg-slate-900">
        <Filters f={f} set={set} toggleList={toggleList} reset={reset} />
      </aside>
      <section className="flex-1 min-w-0 flex flex-col">
        <div className="flex flex-wrap items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2.5 border-b border-slate-200 dark:border-slate-800">
          <Button data-testid="mobile-filters-btn" size="sm" variant="outline" className="h-7 text-xs lg:hidden" onClick={() => setFiltersOpen(true)}><SlidersHorizontal className="h-3.5 w-3.5 mr-1" />Filtres</Button>
          <h1 className="font-display text-lg font-bold tracking-tight">Événements</h1>
          <span className="font-mono text-xs text-slate-500" data-testid="events-total">{data?.total ?? "…"} résultat(s){isFetching && " · actualisation"}</span>
          {(f.organizer_id || f.venue_id || f.source_id) && <Button size="sm" variant="outline" className="h-6 text-[10px]" onClick={() => set({ organizer_id: "", venue_id: "", source_id: "" })}>Filtre entité actif ✕</Button>}
          <div className="ml-auto flex items-center gap-1.5">
            <Button data-testid="add-event-btn" size="sm" variant="outline" className="h-7 text-xs" onClick={() => setAddOpen(true)}><Plus className="h-3.5 w-3.5 mr-1" />Ajouter</Button>
            {["csv", "xlsx", "json"].map((fmt) => (
              <a key={fmt} href={exportUrl(fmt)} data-testid={`export-${fmt}`} className="inline-flex items-center gap-1 h-7 px-2 text-xs border border-slate-200 dark:border-slate-700 rounded-[3px] hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors duration-150">
                <Download className="h-3 w-3" />{fmt.toUpperCase()}
              </a>
            ))}
          </div>
        </div>
        <div className="flex-1 overflow-auto">
          <EventTable items={data?.items || []} sort={sort} order={order} onSort={onSort} />
        </div>
        <div className="flex items-center justify-end gap-2 px-4 py-1.5 border-t border-slate-200 dark:border-slate-800 text-xs">
          <Button data-testid="page-prev" size="sm" variant="ghost" className="h-6" disabled={page <= 1} onClick={() => set({ page: page - 1 })}><ChevronLeft className="h-3.5 w-3.5" /></Button>
          <span className="font-mono">{page} / {pages}</span>
          <Button data-testid="page-next" size="sm" variant="ghost" className="h-6" disabled={page >= pages} onClick={() => set({ page: page + 1 })}><ChevronRight className="h-3.5 w-3.5" /></Button>
        </div>
      </section>
      <ManualEventDialog open={addOpen} onOpenChange={setAddOpen} />
      <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
        <SheetContent side="left" aria-describedby={undefined} className="w-[88vw] max-w-sm p-4 overflow-y-auto safe-top safe-bottom" data-testid="mobile-filters-sheet">
          <SheetTitle className="font-display text-base mb-2">Filtres</SheetTitle>
          <Filters f={f} set={set} toggleList={toggleList} reset={reset} />
          <Button className="w-full mt-4" size="sm" data-testid="mobile-filters-apply" onClick={() => setFiltersOpen(false)}>Voir {data?.total ?? ""} résultat(s)</Button>
        </SheetContent>
      </Sheet>
    </div>
  );
}
